import { type FuseOptionKey } from 'fuse.js';
import { type FC } from 'react';
import { RiSearchLine, RiSyringeLine } from 'react-icons/ri';
import { Link } from 'react-router-dom';

import Icon from '@/components/UI/Icon/Icon';
import type { components } from '@/generated/internal-api/openapi-types';
import { _isNotEmpty } from '@/littledash';
import { RouteService } from '@/support/RouteService';
import { ModalContainer, ModalHeader, useModalAction } from '@/utils/modal';

import styles from './TreatmentStudies.module.scss';
import { useFuse } from '@/support/Hooks/utils/useFuse';

type TreatmentTypeGlossaryStudy = components['schemas']['TreatmentTypeGlossaryStudyV1.schema'];

const SEARCH_THRESHOLD = 10;
const SEARCH_KEYS: Array<FuseOptionKey<TreatmentTypeGlossaryStudy>> = [{ name: 'name' }];

interface TreatmentStudiesProps {
  treatmentName: string;
  studies: Array<TreatmentTypeGlossaryStudy>;
  testId?: string;
}

/**
 * Modal listing every study a treatment type is used in. A treatment can be used in hundreds of
 * studies, so the list scrolls and a fuzzy name search appears once the list is long enough to need one.
 */
const TreatmentStudies: FC<TreatmentStudiesProps> = ({
  treatmentName,
  studies,
  testId = 'treatment-studies-modal',
}) => {
  const { closeModal } = useModalAction();

  const { results, filter, updateFilter } = useFuse({ entries: studies, keys: SEARCH_KEYS });

  const showSearch = studies.length > SEARCH_THRESHOLD;

  return (
    <ModalContainer size="narrow" testKey={testId}>
      <ModalHeader
        title={
          <span className="flex items-center">
            <RiSyringeLine size={18} className="mr2 flex-none" aria-hidden />
            {treatmentName}
          </span>
        }
        subText={`${studies.length} ${studies.length === 1 ? 'study' : 'studies'}`}
        closeModal={closeModal}
      />
      <div data-test-component="TreatmentStudies" data-testid={testId}>
        {showSearch && (
          <div className="pa3 bb b--moon-gray">
            <div className={styles.searchField}>
              <RiSearchLine size={16} className={styles.searchIcon} aria-hidden />
              <input
                type="search"
                value={filter}
                onChange={(event) => updateFilter(event.target.value)}
                placeholder="Search by study name..."
                aria-label="Search studies by name"
                className={`w-100 ${styles.filterInput}`}
                data-testid="treatment-studies-filter"
              />
            </div>
          </div>
        )}
        <div className={styles.studyList} data-test-element="study-list">
          {_isNotEmpty(results) ? (
            <ul className="list ma0 pa2">
              {results.map(({ api_id, name }) => (
                <li key={api_id} data-test-element="study-item" data-test-key={api_id}>
                  <Link
                    to={RouteService.web({ route: 'studies.show', path: { id: api_id } }).route}
                    onClick={closeModal}
                    className={`link blue flex items-center ${styles.studyLink}`}
                  >
                    <Icon icon="view_studies" width={18} height={18} viewBox="0 0 32 32" className="mr2 flex-none" />
                    {name}
                  </Link>
                </li>
              ))}
            </ul>
          ) : (
            <p className="pa3 ma0 dark-gray tc" data-test-element="empty-state">
              {`No studies match "${filter}"`}
            </p>
          )}
        </div>
      </div>
    </ModalContainer>
  );
};

export default TreatmentStudies;
